import { Fragment, useEffect, useState } from 'react';
import { Menu, Transition } from '@headlessui/react';
import { ChevronDownIcon, CalendarDaysIcon } from '@heroicons/react/20/solid';
import DatePicker from 'react-datepicker';
import es from 'date-fns/locale/es';
import 'react-datepicker/dist/react-datepicker.css';
import '../inputDateModern/styles.css';
import { formatDate } from '../../common/formatDateText';
import { toast } from 'react-hot-toast';
import { Button } from '../buttons/Button';
import { createRangeCalendarHeader } from './RangeCalendarHeader';

const MONTHS_SHOWN = 2;
const renderMonthYearHeader = createRangeCalendarHeader(MONTHS_SHOWN);

const DEFAULT_POPOVER_TITLE = 'Filtrar por fechas';
const DEFAULT_POPOVER_SUBTITLE = 'Elige un rango o una fecha de inicio para acotar los resultados.';
const DEFAULT_EMPTY_SUMMARY = 'Seleccionar fechas';

export const PopoverRangeDate = ({
	state,
	setState,
	properties,
	handleApllyFilters,
	handleRemoveFilter,
	hideActionButtons = false,
	popoverTitle = DEFAULT_POPOVER_TITLE,
	popoverSubtitle = DEFAULT_POPOVER_SUBTITLE,
	emptySummaryLabel = DEFAULT_EMPTY_SUMMARY,
	showMonthYearSelects = false,
}) => {
    const normalizeDateValue = (value) => {
        if (!value) return null;
        if (value instanceof Date) return value;
        const parsed = new Date(value);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
    };

    const [startDate, setStartDate] = useState(normalizeDateValue(state?.[properties[0]]));
    const [endDate, setEndDate] = useState(normalizeDateValue(state?.[properties[1]]));
    const [selectionMode, setSelectionMode] = useState('range');
    const modeOptions = [
        { id: 'range', label: 'Rango' },
        { id: 'startOnly', label: 'Desde' },
    ];
    const activeModeIndex = modeOptions.findIndex((option) => option.id === selectionMode);
    const hasAppliedDates = Boolean(
        normalizeDateValue(state?.[properties[0]]) || normalizeDateValue(state?.[properties[1]])
    );

    const updateDates = (nextStartDate, nextEndDate) => {
        setStartDate(nextStartDate);
        setEndDate(nextEndDate);
        setState({
            ...state,
            [properties[0]]: nextStartDate,
            [properties[1]]: nextEndDate,
        });
    };

    useEffect(() => {
        const nextStart = normalizeDateValue(state?.[properties[0]]);
        const nextEnd = normalizeDateValue(state?.[properties[1]]);
        setStartDate(nextStart);
        setEndDate(nextEnd);
    }, [properties, state]);

    const handlePickerChange = (value) => {
        if (selectionMode === 'range') {
            const [nextStartDate, nextEndDate] = value || [null, null];
            updateDates(nextStartDate, nextEndDate);
            return;
        }

        updateDates(value, null);
    };

    const handleModeChange = (mode) => {
        setSelectionMode(mode);
        if (mode === 'range') return;
        updateDates(startDate, null);
    };

    const validateDates = () => {
        if (startDate && endDate && startDate > endDate) {
            toast.error('El rango de fechas no es válido.');
            return false;
        }

        if (!startDate && !endDate) {
            toast.error('Elige una fecha de inicio o una fecha de fin.');
            return false;
        }

        return true;
    };

    const handleApply = () => {
        if (validateDates()) {
            handleApllyFilters(state);
        }
    };

    const handleClear = () => {
        updateDates(null, null);
        setSelectionMode('range');
        handleRemoveFilter();
    };

    const getSummaryLabel = () => {
        if (startDate && endDate) return `${formatDate(startDate)} - ${formatDate(endDate)}`;
        if (startDate) return `Desde ${formatDate(startDate)}`;
        if (endDate) return `Hasta ${formatDate(endDate)}`;
        return emptySummaryLabel;
    };

    const getFooterStatus = () => {
        if (selectionMode === 'range') {
            if (startDate && endDate) return `${formatDate(startDate)} → ${formatDate(endDate)}`;
            if (startDate) return `Desde ${formatDate(startDate)} · elige fin`;
            return 'Selecciona el inicio del rango';
        }
        if (startDate) return `Desde ${formatDate(startDate)}`;
        return 'Selecciona una fecha de inicio';
    };

    return (
        <div className="flex w-full text-center justify-center">
            <Menu as="div" className="w-full relative inline-block text-left">
                <div>
                    <Menu.Button
                        className={`inline-flex w-full justify-center items-center rounded-md bg-white px-4 py-2 text-md font-medium shadow-sm ring-1 ring-inset ${hasAppliedDates ? 'ring-gray-400 text-gray-900' : 'ring-gray-300 text-gray-700'} hover:bg-gray-50 hover:text-gray-900 hover:ring-gray-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2`}
                    >
                        <CalendarDaysIcon className='mr-2 h-4 w-4 text-gray-500' aria-hidden='true' />
                        {getSummaryLabel()}
                        <ChevronDownIcon className="-mr-1 ml-2 h-5 w-5 text-gray-500" aria-hidden="true" />
                    </Menu.Button>
                </div>

                <Transition
                    as={Fragment}
                    enter="transition ease-out duration-150"
                    enterFrom="transform opacity-0 scale-95 translate-y-1"
                    enterTo="transform opacity-100 scale-100 translate-y-0"
                    leave="transition ease-in duration-100"
                    leaveFrom="transform opacity-100 scale-100 translate-y-0"
                    leaveTo="transform opacity-0 scale-95 translate-y-1"
                >
                    <Menu.Items className="absolute right-0 mt-2 w-full md:w-[760px] origin-top-right overflow-hidden rounded-2xl bg-white shadow-xl shadow-gray-900/10 ring-1 ring-gray-200 focus:outline-none z-50">
                        <div className='flex items-start gap-3 bg-gradient-to-br from-indigo-50/70 via-white to-white px-5 py-4 border-b border-gray-100'>
                            <div className='flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-indigo-100 text-indigo-600'>
                                <CalendarDaysIcon className='h-5 w-5' aria-hidden='true' />
                            </div>
                            <div className='text-left'>
                                <h3 className='text-sm font-semibold text-gray-900'>{popoverTitle}</h3>
                                <p className='mt-0.5 text-xs text-gray-500 leading-snug'>
                                    {popoverSubtitle}
                                </p>
                            </div>
                        </div>

                        <div className="p-4 pb-2">
                            <div className="relative inline-grid w-max grid-cols-2 rounded-full bg-gray-100 p-1 ring-1 ring-inset ring-gray-200 shadow-[inset_0_1px_2px_rgba(15,23,42,0.06)]">
                                <span
                                    className="absolute inset-y-1 left-1 w-[calc((100%-0.5rem)/2)] rounded-full bg-indigo-600 shadow-md shadow-indigo-600/30 ring-1 ring-indigo-500/40 transition-transform duration-300 ease-out"
                                    style={{ transform: `translateX(${Math.max(activeModeIndex, 0) * 100}%)` }}
                                    aria-hidden="true"
                                />
                                {
                                    modeOptions.map((option) => (
                                        <button
                                            key={option.id}
                                            type='button'
                                            onClick={() => handleModeChange(option.id)}
                                            className={`relative z-10 rounded-full px-4 py-1.5 text-sm font-medium transition-colors duration-200 ${selectionMode === option.id ? 'text-white' : 'text-gray-600 hover:text-gray-900'}`}
                                        >
                                            {option.label}
                                        </button>
                                    ))
                                }
                            </div>
                        </div>

                        <div key={selectionMode} className='px-4 pb-2 calendar-mode-panel'>
                            {
                                selectionMode === 'range' ? (
                                    <DatePicker
                                        locale={es}
                                        inline
                                        monthsShown={MONTHS_SHOWN}
                                        selected={startDate}
                                        startDate={startDate}
                                        endDate={endDate}
                                        onChange={handlePickerChange}
                                        selectsRange
                                        calendarClassName="modern-calendar modern-calendar-range"
                                        renderCustomHeader={showMonthYearSelects ? renderMonthYearHeader : undefined}
                                    />
                                ) : (
                                    <DatePicker
                                        locale={es}
                                        inline
                                        monthsShown={MONTHS_SHOWN}
                                        selected={startDate}
                                        onChange={handlePickerChange}
                                        calendarClassName="modern-calendar modern-calendar-range"
                                        renderCustomHeader={showMonthYearSelects ? renderMonthYearHeader : undefined}
                                    />
                                )
                            }
                        </div>

                        <div className='flex items-center justify-between gap-3 border-t border-gray-100 bg-gray-50/60 px-5 py-3'>
                            <span className='text-xs text-gray-500 capitalize'>
                                {getFooterStatus()}
                            </span>
                            {
                                !hideActionButtons && (
                                    <div className='flex items-center gap-2'>
                                        <Button variant='secondary' onClick={handleClear}>
                                            Limpiar
                                        </Button>
                                        <Button variant='primary' onClick={handleApply}>
                                            Aplicar
                                        </Button>
                                    </div>
                                )
                            }
                        </div>
                    </Menu.Items>
                </Transition>
            </Menu>
        </div>
    );
};
